import React, {
  FC, useState
} from 'react';
import {
  useTranslation
} from 'react-i18next';
import {
  useForm
} from 'react-hook-form';
import clsx from 'clsx';

import {
  EmptyResultsMessage,
  Spinner,
  successfulToast,
  TableFooter,
  usePaginationSettings,
  Button,
  Input,
  InputType,
  ModalContainer,
  PopoverMenu,
  Switcher,
} from 'src/shared/components';
import {
  showWarningFromServer, formatDateRange
} from 'src/shared/utils';
import {
  useGetApiOrganizationsByIdSuggestionsQuery,
  useDeleteApiOrganizationsByIdSuggestionsAndSuggestionIdMutation,
  useDeleteApiOrganizationsByIdSuggestionsBulkMutation,
  useGetApiOrganizationsByIdSuggestionsSettingsQuery,
  usePatchApiOrganizationsByIdSuggestionsSettingsMutation,
  QuestionSuggestionItem,
} from 'src/redux/openapi';
import {
  CalendarIcon,
  DownloadIcon,
  TrashDeleteIcon,
  LinkIcon,
  MoreVerticalIcon,
  EditPencilIcon,
} from 'src/shared/icons';
import {
  DatePickerMenu, TDate
} from 'src/features/DatePicker';

interface SuggestionsContentProps {
  organizationId: string;
  searchQuery: string;
}

interface ScoreForm {
  suggestionGoodieScore: string;
}

interface TitleForm {
  suggestionTitle: string;
}

export const SuggestionsContent: FC<SuggestionsContentProps> = ({
  organizationId,
  searchQuery,
}) => {
  const {
    t,
    i18n: {
      language
    },
  } = useTranslation();

  const {
    currentPage, setCurrentPage, pageSize, onPageSizeChange
  } = usePaginationSettings(searchQuery);

  const [exportFormat, setExportFormat] = useState<'xlsx' | 'csv'>('xlsx');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isScoreModalOpen, setIsScoreModalOpen] = useState(false);
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const [deleteSingleId, setDeleteSingleId] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<TDate[] | null>(null);

  const [exportDateRange, setExportDateRange] = useState<TDate[] | null>(null);
  const [deleteDateRange, setDeleteDateRange] = useState<TDate[] | null>(null);

  const [closeDateFilter, setCloseDateFilter] = useState(false);
  const [closeExportPicker, setCloseExportPicker] = useState(false);
  const [closeDeletePicker, setCloseDeletePicker] = useState(false);

  const scoreForm = useForm<ScoreForm>({
    defaultValues: {
      suggestionGoodieScore: '',
    },
  });

  const titleForm = useForm<TitleForm>({
    defaultValues: {
      suggestionTitle: '',
    },
  });

  const queryDateFrom = dateFilter?.[0]?.toISOString();
  const queryDateTo = dateFilter?.[1]?.toISOString();

  const {
    data, isLoading, refetch
  } = useGetApiOrganizationsByIdSuggestionsQuery(
    {
      id: organizationId,
      page: currentPage,
      limit: pageSize,
      dateFrom: queryDateFrom,
      dateTo: queryDateTo,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: settingsData, refetch: refetchSettings
  } = useGetApiOrganizationsByIdSuggestionsSettingsQuery({
    id: organizationId,
  });

  const settings = settingsData?.data;

  const suggestions: QuestionSuggestionItem[] = data?.data?.suggestions ?? [];

  const total = data?.data?.total ?? 0;

  const [deleteSingle] = useDeleteApiOrganizationsByIdSuggestionsAndSuggestionIdMutation();

  const [deleteBulk] = useDeleteApiOrganizationsByIdSuggestionsBulkMutation();

  const [updateSettings, {
    isLoading: isSettingsLoading
  }] = usePatchApiOrganizationsByIdSuggestionsSettingsMutation();

  const handleDeleteSingle = async (id: string) => {
    try {
      const res = await deleteSingle({
        id: organizationId,
        suggestionId: id,
      }).unwrap();

      successfulToast(res.message);
      refetch();
      setDeleteSingleId(null);
    } catch (error) {
      showWarningFromServer(error);
    }
  };

  const handleDeleteByRange = async () => {
    if (!deleteDateRange?.[0] || !deleteDateRange?.[1]) {
      return;
    }

    try {
      const res = await deleteBulk({
        id: organizationId,
        body: {
          dateFrom: deleteDateRange[0].toISOString(),
          dateTo: deleteDateRange[1].toISOString(),
        },
      }).unwrap();

      successfulToast(res.message);
      refetch();
      setIsDeleteModalOpen(false);
      setDeleteDateRange(null);
    } catch (error) {
      showWarningFromServer(error);
    }
  };

  const handleExport = () => {
    const searchParams = new URLSearchParams({
      format: exportFormat,
    });

    if (exportDateRange?.[0]) {
      searchParams.set(
        'dateFrom',
        exportDateRange[0].toISOString()
      );
    }

    if (exportDateRange?.[1]) {
      searchParams.set(
        'dateTo',
        exportDateRange[1].toISOString()
      );
    }

    const baseUrl = process.env.REACT_APP_API_PATH ?? '';

    const url = `${baseUrl}/api/organizations/${organizationId}/suggestions/export?${searchParams.toString()}`;

    window.open(
      url,
      '_blank'
    );

    setIsExportModalOpen(false);
    setExportDateRange(null);
  };

  const handleSaveScore = scoreForm.handleSubmit(
    async ({
      suggestionGoodieScore
    }) => {
      const score = suggestionGoodieScore
        ? Number(suggestionGoodieScore)
        : null;

      if (score !== null && score <= 0) {
        scoreForm.setError(
          'suggestionGoodieScore',
          {
            message: t('suggestions.scoreValidation'),
          }
        );

        return;
      }

      try {
        const res = await updateSettings({
          id: organizationId,
          body: {
            suggestionsEnabled: settings?.suggestionsEnabled ?? false,
            suggestionGoodieScore: score,
            suggestionTitle: settings?.suggestionTitle ?? null,
          },
        }).unwrap();

        successfulToast(res.message);
        refetchSettings();
        setIsScoreModalOpen(false);
      } catch (error) {
        showWarningFromServer(error);
      }
    },
  );

  const handleToggleSuggestions = async () => {
    try {
      const res = await updateSettings({
        id: organizationId,
        body: {
          suggestionsEnabled: !(settings?.suggestionsEnabled ?? false),
          suggestionGoodieScore: settings?.suggestionGoodieScore ?? null,
          suggestionTitle: settings?.suggestionTitle ?? null,
        },
      }).unwrap();

      successfulToast(res.message);
      refetchSettings();
    } catch (error) {
      showWarningFromServer(error);
    }
  };

  const openScoreModal = () => {
    scoreForm.reset({
      suggestionGoodieScore: settings?.suggestionGoodieScore?.toString() ?? '',
    });

    setIsScoreModalOpen(true);
  };

  const openTitleModal = () => {
    titleForm.reset({
      suggestionTitle: settings?.suggestionTitle ?? '',
    });

    setIsTitleModalOpen(true);
  };

  const handleSaveTitle = titleForm.handleSubmit(
    async ({
      suggestionTitle
    }) => {
      try {
        const res = await updateSettings({
          id: organizationId,
          body: {
            suggestionsEnabled: settings?.suggestionsEnabled ?? false,
            suggestionGoodieScore: settings?.suggestionGoodieScore ?? null,
            suggestionTitle: suggestionTitle.trim() || null,
          },
        }).unwrap();

        successfulToast(res.message);
        refetchSettings();
        setIsTitleModalOpen(false);
      } catch (error) {
        showWarningFromServer(error);
      }
    },
  );

  const handleDateFilterApply = (dates: TDate[] | null) => {
    setDateFilter(dates);
    setCurrentPage(1);
    setCloseDateFilter(true);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(
      2,
      '0'
    );

    const month = String(date.getMonth() + 1).padStart(
      2,
      '0'
    );

    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(
      2,
      '0'
    );

    const minutes = String(date.getMinutes()).padStart(
      2,
      '0'
    );

    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  const getSuggestionUserLabel = (suggestion: QuestionSuggestionItem) => {
    return (
      suggestion.user.displayName
      || suggestion.user.fullName
      || suggestion.user.email
      || '\u2014'
    );
  };

  const getSuggestionPageLabel = (pageUrl: string) => {
    try {
      const parsedUrl = new URL(pageUrl);

      return (
        `${parsedUrl.pathname}${parsedUrl.search}`.replace(
          /^\/+/,
          ''
        )
        || parsedUrl.hostname
      );
    } catch {
      return pageUrl.replace(
        /^https?:\/\//,
        ''
      );
    }
  };

  const getDateRangeDisplay = (dates: TDate[] | null) => {
    if (!dates?.[0]) {
      return '';
    }

    return formatDateRange({
      start: dates[0],
      end: dates[1],
      language,
    });
  };

  if (isLoading) {
    return <Spinner withLoadingText />;
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-4 shrink-0">
        <h2 className="text-28-36 font-bold text-black capitalize">
          {t('suggestions.suggestions')}
        </h2>

        <div className="flex items-center gap-4">
          <PopoverMenu
            transparent
            placement="bottom-end"
            borderRadius={24}
            shouldForcedClose={closeDateFilter}
            onClose={() => setCloseDateFilter(false)}
            renderCustomButton={({
              isOpen, toggleMenu, triggerProps
            }) => (
              <button
                type="button"
                onClick={toggleMenu}
                aria-label={t('suggestions.dateTime')}
                className={clsx(
                  'w-11 h-11 flex items-center justify-center',
                  'rounded-full border',
                  {
                    'border-button-blue text-button-blue': isOpen || dateFilter,
                    'border-gray90 text-dim-gray': !isOpen && !dateFilter,
                  },
                )}
                {...triggerProps}
              >
                <CalendarIcon className="w-5 h-5" />
              </button>
            )}
          >
            <DatePickerMenu
              onApply={handleDateFilterApply}
              selectedDateRange={dateFilter}
              clearButtonTitle={t('buttons.clear')}
              applyButtonTitle={t('buttons.applyFilter')}
            />
          </PopoverMenu>

          <Button
            variant="big-black"
            className={clsx(
              '!w-auto !h-auto !py-2.5 !px-5',
              '!text-sm !font-semibold',
            )}
            onClick={() => setIsExportModalOpen(true)}
          >
            {t('suggestions.export')}

            <DownloadIcon className="w-4 h-4 ml-1.5 text-white" />
          </Button>

          <Button
            variant="big-blue"
            className={clsx(
              '!w-auto !h-auto !py-2.5 !px-5',
              '!text-sm !font-semibold',
            )}
            onClick={() => setIsDeleteModalOpen(true)}
          >
            {t('buttons.delete')}

            <TrashDeleteIcon className="w-4 h-4 ml-1.5 text-white" />
          </Button>

          <PopoverMenu
            placement="bottom-end"
            customShift={22}
            renderCustomButton={({
              isOpen, toggleMenu, triggerProps
            }) => (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleMenu();
                }}
                {...triggerProps}
              >
                <MoreVerticalIcon
                  className={clsx(
                    'w-6 h-6',
                    {
                      'text-dim-gray': !isOpen,
                      'text-button-blue': isOpen,
                    }
                  )}
                />
              </button>
            )}
          >
            <div className="p-3 flex flex-col gap-2 shadow-menu-shadow bg-white rounded-md min-w-[240px]">
              <button
                type="button"
                className={clsx(
                  'flex items-center gap-2 p-2',
                  'font-medium text-15-20 text-dark-gray',
                  'hover:bg-gray-50 rounded-md w-full text-left',
                )}
                onClick={openTitleModal}
              >
                <EditPencilIcon className="w-5 h-5" />

                {t('suggestions.setTitle')}
              </button>

              <div className="h-px bg-gray94" />

              <button
                type="button"
                className={clsx(
                  'flex items-center gap-2 p-2',
                  'font-medium text-15-20 text-dark-gray',
                  'hover:bg-gray-50 rounded-md w-full text-left',
                )}
                onClick={openScoreModal}
              >
                <EditPencilIcon className="w-5 h-5" />

                {t('suggestions.setSuggestionScore')}
              </button>

              <div className="h-px bg-gray94" />

              <div className="p-2">
                <Switcher
                  isActive={settings?.suggestionsEnabled ?? false}
                  onChange={handleToggleSuggestions}
                  label={t('suggestions.enableSuggestions')}
                  isLoading={isSettingsLoading}
                  labelClassName="!flex-row-reverse !justify-between w-full"
                />
              </div>
            </div>
          </PopoverMenu>
        </div>
      </div>

      <div className="flex flex-col flex-1 min-h-0">
        {!suggestions.length ? (
          <EmptyResultsMessage
            results={t('suggestions.suggestions').toLowerCase()}
          />
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="w-full">
                <div className="sticky top-0 z-10 bg-white border-b border-gray-whisper flex items-center py-2 px-3">
                  <p className="w-[14%] text-xs leading-5 font-bold text-dark-gray">
                    {t('suggestions.user')}
                  </p>

                  <p className="flex-1 text-xs leading-5 font-bold text-dark-gray">
                    {t('suggestions.suggestion')}
                  </p>

                  <p className="w-[16%] text-xs leading-5 font-bold text-dark-gray">
                    {t('suggestions.pageUrl')}
                  </p>

                  <p className="w-[112px] text-xs leading-5 font-bold text-dark-gray whitespace-nowrap">
                    {t('suggestions.dateTime')}
                  </p>

                  <div className="w-11 shrink-0" />
                </div>

                {suggestions.map((s: QuestionSuggestionItem) => (
                  <div
                    key={s.id}
                    className="border-b border-gray-whisper flex items-center gap-2 min-h-[72px] px-3 py-3"
                  >
                    <p className="w-[14%] text-sm font-bold text-dark-gray pr-3 truncate">
                      {getSuggestionUserLabel(s)}
                    </p>

                    <p className="flex-1 text-sm font-medium text-dim-gray pr-4 line-clamp-2 min-w-0">
                      {s.text}
                    </p>

                    <div className="w-[16%] flex items-center gap-2 min-w-0 shrink-0">
                      <a
                        href={s.pageUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-medium text-dim-gray underline underline-offset-2 truncate min-w-0"
                        title={s.pageUrl}
                      >
                        {getSuggestionPageLabel(s.pageUrl)}
                      </a>

                      <a
                        href={s.pageUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 text-dim-gray hover:text-button-blue transition-colors"
                        aria-label={t('suggestions.pageUrl')}
                      >
                        <LinkIcon className="w-5 h-5" />
                      </a>
                    </div>

                    <p className="w-[112px] shrink-0 text-sm font-medium text-dim-gray whitespace-nowrap">
                      {formatDateTime(s.createdAt)}
                    </p>

                    <div className="w-11 shrink-0 flex justify-end">
                      <button
                        onClick={() => setDeleteSingleId(s.id)}
                        className={clsx(
                          'w-9 h-9 rounded-full border border-gray90',
                          'text-dim-gray hover:text-red-text-error',
                          'flex items-center justify-center shadow-menu-shadow transition-colors',
                        )}
                        type="button"
                        aria-label={t('buttons.delete')}
                      >
                        <TrashDeleteIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <TableFooter
              totalCount={total}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onPageSizeChange={onPageSizeChange}
              selectorLabel={t('suggestions.suggestions')}
            />
          </>
        )}
      </div>

      <ModalContainer
        isOpen={!!deleteSingleId}
        modalTitle={t('buttons.delete')}
        closeModalHandler={() => setDeleteSingleId(null)}
        modalWidth={420}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700">
            {t('suggestions.deleteSuggestionConfirm')}
          </p>

          <div className="flex justify-end gap-2">
            <Button
              variant="big-grey-bordered"
              className="!w-auto"
              onClick={() => setDeleteSingleId(null)}
            >
              {t('buttons.cancel')}
            </Button>

            <Button
              variant="big-blue"
              className="!w-auto"
              onClick={() => {
                if (deleteSingleId) {
                  handleDeleteSingle(deleteSingleId);
                }
              }}
            >
              {t('buttons.delete')}
            </Button>
          </div>
        </div>
      </ModalContainer>

      <ModalContainer
        isOpen={isDeleteModalOpen}
        modalTitle={t('suggestions.deleteByDateRange')}
        closeModalHandler={() => setIsDeleteModalOpen(false)}
        modalWidth={460}
      >
        <div className="flex flex-col gap-4">
          <PopoverMenu
            zIndex={10000}
            placement="bottom-end"
            shouldForcedClose={closeDeletePicker}
            onClose={() => setCloseDeletePicker(false)}
            renderCustomButton={({
              triggerProps, toggleMenu
            }) => (
              <div {...triggerProps}>
                <Input
                  value={getDateRangeDisplay(deleteDateRange)}
                  placeholder={t('suggestions.selectDateRange')}
                  label={t('suggestions.dateRange')}
                  isRequired
                  renderRightContent={() => (
                    <div className="pr-3">
                      <CalendarIcon className="w-5 h-5 text-dim-gray" />
                    </div>
                  )}
                  onClick={() => toggleMenu()}
                />
              </div>
            )}
          >
            <DatePickerMenu
              onApply={(dates) => {
                setDeleteDateRange(dates);
                setCloseDeletePicker(true);
              }}
              selectedDateRange={deleteDateRange}
              clearButtonTitle={t('buttons.clear')}
              applyButtonTitle={t('buttons.applyFilter')}
            />
          </PopoverMenu>

          <div className="flex justify-end gap-2">
            <Button
              variant="big-grey-bordered"
              className="!w-auto"
              type="button"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              {t('buttons.cancel')}
            </Button>

            <Button
              variant="big-red"
              className="!w-auto"
              type="button"
              disabled={!deleteDateRange?.[0] || !deleteDateRange?.[1]}
              onClick={handleDeleteByRange}
            >
              {t('buttons.delete')}
            </Button>
          </div>
        </div>
      </ModalContainer>

      <ModalContainer
        isOpen={isExportModalOpen}
        modalTitle={t('suggestions.exportSuggestions')}
        closeModalHandler={() => setIsExportModalOpen(false)}
        modalWidth={460}
      >
        <div className="flex flex-col gap-4">
          <PopoverMenu
            zIndex={10000}
            placement="bottom-end"
            shouldForcedClose={closeExportPicker}
            onClose={() => setCloseExportPicker(false)}
            renderCustomButton={({
              triggerProps, toggleMenu
            }) => (
              <div {...triggerProps}>
                <Input
                  value={getDateRangeDisplay(exportDateRange)}
                  placeholder={t('suggestions.selectDateRange')}
                  label={t('suggestions.dateRange')}
                  renderRightContent={() => (
                    <div className="pr-3">
                      <CalendarIcon className="w-5 h-5 text-dim-gray" />
                    </div>
                  )}
                  onClick={() => toggleMenu()}
                />
              </div>
            )}
          >
            <DatePickerMenu
              onApply={(dates) => {
                setExportDateRange(dates);
                setCloseExportPicker(true);
              }}
              selectedDateRange={exportDateRange}
              clearButtonTitle={t('buttons.clear')}
              applyButtonTitle={t('buttons.applyFilter')}
            />
          </PopoverMenu>

          <div className="flex flex-col gap-1">
            <span className="block text-13-21 md:text-15-20 font-medium text-grey-dark-40">
              {t('suggestions.selectFormat')}
            </span>

            <div className="flex gap-3 mt-2">
              {(['xlsx', 'csv'] as const).map((fmt) => (
                <label
                  key={fmt}
                  className="flex items-center gap-1.5 text-sm cursor-pointer"
                >
                  <input
                    type="radio"
                    value={fmt}
                    checked={exportFormat === fmt}
                    onChange={() => setExportFormat(fmt)}
                  />

                  {fmt.toUpperCase()}
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="big-grey-bordered"
              className="!w-auto"
              type="button"
              onClick={() => setIsExportModalOpen(false)}
            >
              {t('buttons.cancel')}
            </Button>

            <Button
              variant="big-blue"
              className="!w-auto"
              type="button"
              onClick={handleExport}
            >
              {t('suggestions.exportAll')}
            </Button>
          </div>
        </div>
      </ModalContainer>

      <ModalContainer
        isOpen={isScoreModalOpen}
        modalTitle={t('suggestions.setSuggestionScore')}
        closeModalHandler={() => setIsScoreModalOpen(false)}
        modalWidth={460}
      >
        <form
          onSubmit={handleSaveScore}
          className="flex flex-col gap-4"
        >
          <Input
            type={InputType.NUMBER}
            value={scoreForm.watch('suggestionGoodieScore')}
            onChange={(val) => {
              scoreForm.setValue(
                'suggestionGoodieScore',
                val
              );

              scoreForm.clearErrors('suggestionGoodieScore');
            }}
            label={t('suggestions.points')}
            placeholder={t('suggestions.goodieScorePlaceholder')}
            errorMessage={
              scoreForm.formState.errors.suggestionGoodieScore?.message
            }
          />

          <div className="flex justify-end gap-2">
            <Button
              variant="big-grey-bordered"
              type="button"
              className="!w-auto"
              onClick={() => setIsScoreModalOpen(false)}
            >
              {t('buttons.cancel')}
            </Button>

            <Button
              variant="big-blue"
              type="submit"
              className="!w-auto"
            >
              {t('buttons.save')}
            </Button>
          </div>
        </form>
      </ModalContainer>

      <ModalContainer
        isOpen={isTitleModalOpen}
        modalTitle={t('suggestions.setTitle')}
        closeModalHandler={() => setIsTitleModalOpen(false)}
        modalWidth={460}
      >
        <form
          onSubmit={handleSaveTitle}
          className="flex flex-col gap-4"
        >
          <Input
            value={titleForm.watch('suggestionTitle')}
            onChange={(val) => titleForm.setValue(
              'suggestionTitle',
              val
            )}
            label={t('suggestions.dialogTitle')}
            placeholder={t('suggestions.dialogTitlePlaceholder')}
          />

          <div className="flex justify-end gap-2">
            <Button
              variant="big-grey-bordered"
              type="button"
              className="!w-auto"
              onClick={() => setIsTitleModalOpen(false)}
            >
              {t('buttons.cancel')}
            </Button>

            <Button
              variant="big-blue"
              type="submit"
              className="!w-auto"
            >
              {t('buttons.save')}
            </Button>
          </div>
        </form>
      </ModalContainer>
    </div>
  );
};
